import { Link, Outlet, useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { ArrowUpRight, Check, CircleCheck, CircleX, KeyRound, Minus, RotateCw, Search, UserRound } from "lucide-react"
import { type FormEvent, useEffect, useId, useMemo, useState } from "react"
import { Trans } from "react-i18next"
import { HairlineTable } from "@/components/archetypes/HairlineTable"
import { describeRuleCondition } from "@/components/definition-summary"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState, TableSkeleton, UnavailableBlock } from "@/components/states"
import { ActionBar } from "@/components/ui/action-bar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { controlClass } from "@/components/ui/control"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PageHeader as ContentHeader } from "@/components/ui/page-header"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import { CellUnreachable, refusalOfError } from "@/lib/api"
import { useT } from "@/lib/i18n"
import { parseExact } from "@/lib/lossless"
import {
  accessRules,
  CONJUNCTS,
  type Delegation,
  delegationState,
  type Explanation,
  explainableActions,
  type Membership,
  membershipGrant,
  OUTCOME_COPY,
  ruleAnchor,
  validity,
} from "@/lib/permissions"
import {
  useDefinitions,
  useDelegations,
  useExplain,
  useMemberships,
  useObjects,
  useRevokeDelegation,
} from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"
import { usePrincipal } from "@/widget/runtime"
import { objectTypes } from "./objects"

const PERSON_TYPE = "party.Person"
const CEDAR_ISSUE = 3224

const tab =
  "inline-flex h-8 items-center border-b-2 border-transparent px-1 text-sm text-muted-foreground transition-colors hover:text-foreground data-[status=active]:border-primary data-[status=active]:text-foreground"

export function PermissionsLayout() {
  const t = useT()
  const scope = useScope()
  return (
    <>
      <PageHeader crumbs={[t("nav.permissions")]} />
      <div className="flex-1 overflow-y-auto">
        <Screen width="board" className="px-4 py-6 md:px-6">
          <ContentHeader description={t("permissions.description")} />
          <nav aria-label={t("nav.permissions")} className="mt-5 flex gap-5 border-b border-border-subtle">
            <Link to="/$orgId/$environmentId/permissions/explain" params={scope} className={tab}>
              {t("permissions.whoCanDoWhat")}
            </Link>
            <Link to="/$orgId/$environmentId/permissions/rules" params={scope} className={tab}>
              {t("permissions.accessRules")}
            </Link>
            <Link to="/$orgId/$environmentId/permissions/people" params={scope} className={tab}>
              {t("permissions.people")}
            </Link>
          </nav>
          <div className="pt-6">
            <Outlet />
          </div>
        </Screen>
      </div>
    </>
  )
}

/** Explain one principal × action × resource through POST /v1/authz/explain. */
export function ExplainPage() {
  const t = useT()
  const scope = useScope()
  const id = useId()
  const search = useSearch({ from: "/$orgId/$environmentId/permissions/explain" })
  const defs = useDefinitions(scope)
  const me = usePrincipal(scope)
  const explain = useExplain(scope)
  // Untouched fields fall back to the link's search, then to yourself and the first object type.
  const [principalEdit, setPrincipal] = useState<string | null>(search.principal ?? null)
  const [action, setAction] = useState(search.action ?? "norn.read")
  const [typeEdit, setObjectType] = useState<string | null>(search.type ?? null)
  const [key, setKey] = useState(search.key ?? "")
  const [inputText, setInputText] = useState("")
  const [delegationId, setDelegationId] = useState("")
  const [inputError, setInputError] = useState<string | null>(null)
  const actions = useMemo(() => (defs.data ? explainableActions(defs.data) : ["norn.read"]), [defs.data])
  const types = useMemo(
    () => (defs.data ? objectTypes(defs.data).filter((t) => t.artifactId !== null) : []),
    [defs.data],
  )
  const refused = defs.isError ? refusalOfError(defs.error) : null

  // Explaining yourself is the default; explaining someone else needs norn.authz.explain.
  const self = me.status === "ready" ? me.actor : null
  const principal = principalEdit ?? self ?? ""
  const objectType = typeEdit ?? types[0]?.name ?? ""
  const keys = useObjects(scope, objectType, null, 50, objectType !== "")
  const keyOptions = objectType && keys.data ? keys.data.items.map((o) => o.key) : []

  const submit = (e: FormEvent) => {
    e.preventDefault()
    let input: unknown = null
    if (action.trim() !== "norn.read" && inputText.trim()) {
      try {
        input = parseExact(inputText)
      } catch (error) {
        setInputError(error instanceof Error ? error.message : String(error))
        return
      }
    }
    setInputError(null)
    explain.mutate({
      principal: principal.trim(),
      action: action.trim(),
      resource: { objectType: objectType.trim(), key: key.trim() },
      input,
      delegationId: delegationId.trim() || null,
    })
  }

  return (
    <div className="grid gap-6">
      <Section title={t("permissions.explain.question")}>
        <form onSubmit={submit} className="space-y-8" aria-label={t("permissions.explain.question")}>
          <div className="grid gap-4 md:grid-cols-[1.1fr_1.2fr_1.4fr]">
            <div className="grid gap-1.5">
              <Label htmlFor={`${id}-principal`}>{t("permissions.explain.principal")}</Label>
              <Input
                id={`${id}-principal`}
                value={principal}
                onChange={(e) => setPrincipal(e.target.value)}
                placeholder="person:…"
                className={cn(controlClass, "font-mono text-xs")}
                required
                spellCheck={false}
              />
              <p className="text-2xs text-subtle-foreground">
                {self && principal.trim() !== self
                  ? t("permissions.explain.otherPrincipal")
                  : t("permissions.explain.selfPrincipal")}
              </p>
            </div>
            <div className="grid content-start gap-1.5">
              <Label htmlFor={`${id}-action`}>{t("permissions.explain.may")}</Label>
              <Input
                id={`${id}-action`}
                list={`${id}-actions`}
                value={action}
                onChange={(e) => setAction(e.target.value)}
                className={cn(controlClass, "font-mono text-xs")}
                required
                spellCheck={false}
              />
              <datalist id={`${id}-actions`}>
                {actions.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
            </div>
            <div className="grid content-start gap-1.5">
              <Label htmlFor={`${id}-type`}>{t("definitionSummary.on")}</Label>
              <div className="flex gap-2">
                <Input
                  id={`${id}-type`}
                  aria-label={t("permissions.explain.resourceType")}
                  list={`${id}-types`}
                  value={objectType}
                  onChange={(e) => setObjectType(e.target.value)}
                  placeholder={t("graph.objectType")}
                  className={cn(controlClass, "min-w-0 flex-1 font-mono text-xs")}
                  required
                  spellCheck={false}
                />
                <datalist id={`${id}-types`}>
                  {types.map((t) => (
                    <option key={t.name} value={t.name} />
                  ))}
                </datalist>
                <Input
                  aria-label={t("permissions.explain.resourceKey")}
                  list={`${id}-keys`}
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder={t("permissions.explain.keyPlaceholder")}
                  className={cn(controlClass, "w-32 font-mono text-xs")}
                  required
                  spellCheck={false}
                />
                <datalist id={`${id}-keys`}>
                  {keyOptions.map((k) => (
                    <option key={k} value={k} />
                  ))}
                </datalist>
              </div>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-[2.3fr_1.4fr]">
            {action.trim() !== "norn.read" ? (
              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-input`}>{t("permissions.explain.actionInput")}</Label>
                <textarea
                  id={`${id}-input`}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  rows={3}
                  spellCheck={false}
                  placeholder='{"orderId": "SO-0004"}'
                  className="w-full rounded-md border border-input bg-background px-2 py-1.5 font-mono text-xs text-foreground outline-none focus-visible:border-primary/60 focus-visible:ring-3 focus-visible:ring-ring/40"
                  aria-invalid={inputError !== null}
                  aria-describedby={`${id}-input-help`}
                />
                <p
                  id={`${id}-input-help`}
                  className={cn("text-2xs", inputError ? "text-destructive" : "text-subtle-foreground")}
                >
                  {inputError
                    ? t("permissions.explain.notJson", { error: inputError })
                    : t("permissions.explain.inputHelp")}
                </p>
              </div>
            ) : (
              <p className="self-end text-2xs text-subtle-foreground">{t("permissions.explain.noReadInput")}</p>
            )}
            <div className="grid content-start gap-1.5">
              <Label htmlFor={`${id}-delegation`}>{t("permissions.explain.throughDelegation")}</Label>
              <Input
                id={`${id}-delegation`}
                value={delegationId}
                onChange={(e) => setDelegationId(e.target.value)}
                placeholder={t("permissions.explain.delegationIdPlaceholder")}
                className={cn(controlClass, "font-mono text-xs")}
                spellCheck={false}
              />
              <p className="text-2xs text-subtle-foreground">{t("permissions.explain.delegationHelp")}</p>
            </div>
          </div>
          {defs.isError ? (
            <p role="status" className="text-xs text-warning">
              {refused
                ? t("permissions.explain.definitionsRefused", { code: refused.refusal.code })
                : t("permissions.explain.definitionsUnavailable")}
            </p>
          ) : null}
          <ActionBar
            className="max-md:bottom-0"
            status={<span className="text-xs text-muted-foreground">{t("permissions.explain.snapshotQuestion")}</span>}
          >
            <Button
              type="submit"
              variant="primary"
              disabled={explain.isPending || !principal.trim() || !action.trim() || !objectType.trim() || !key.trim()}
            >
              {explain.isPending ? <RotateCw className="animate-spin" /> : <Search />} {t("permissions.explain.submit")}
            </Button>
          </ActionBar>
        </form>
      </Section>
      {explain.isError ? (
        <ErrorState error={explain.error} what={t("permissions.explain.thisQuestion")} inline />
      ) : explain.data && explain.variables ? (
        <ExplainResult result={explain.data} asked={explain.variables} />
      ) : null}
    </div>
  )
}

function ExplainResult({
  result,
  asked,
}: {
  result: Explanation
  asked: { principal: string; action: string; resource: { objectType: string; key: string } }
}) {
  const t = useT()
  const scope = useScope()
  const allowed = result.decision === "allow"
  return (
    <section
      aria-label={t("permissions.explain.explanation")}
      className="overflow-hidden rounded-lg border border-border-subtle"
    >
      <header
        className={cn(
          "flex items-start gap-3 border-b border-border-subtle px-5 py-4",
          allowed ? "bg-success-soft/40" : "bg-destructive-soft/40",
        )}
      >
        {allowed ? (
          <CircleCheck className="mt-0.5 size-5 text-success" aria-hidden />
        ) : (
          <CircleX className="mt-0.5 size-5 text-destructive" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-medium">
            {allowed ? t("permissions.explain.allowed") : t("permissions.explain.denied")}
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            <Trans
              shouldUnescape
              tOptions={{ interpolation: { escapeValue: true } }}
              t={t}
              i18nKey={allowed ? "permissions.explain.allowedSentence" : "permissions.explain.deniedSentence"}
              values={{
                principal: asked.principal,
                action: asked.action,
                resource: `${asked.resource.objectType} / ${asked.resource.key}`,
              }}
              components={{
                principalValue: <code className="font-mono text-xs text-foreground/90" />,
                actionValue: <code className="font-mono text-xs text-foreground/90" />,
                resourceValue: (
                  <Link
                    to="/$orgId/$environmentId/objects/$objectType/$key"
                    params={{ ...scope, objectType: asked.resource.objectType, key: asked.resource.key }}
                    className="font-mono text-xs text-foreground/90 hover:text-primary"
                  />
                ),
              }}
            />
          </p>
        </div>
        <code className="font-mono text-2xs text-muted-foreground" title={t("permissions.explain.stableReasonCode")}>
          {result.reason}
        </code>
      </header>
      <ol className="px-5 py-2" aria-label={t("permissions.explain.checksOrder")}>
        {CONJUNCTS.map((c, i) => {
          const outcome = result.conjuncts[c.key]
          const copy = OUTCOME_COPY[outcome]
          return (
            <li
              key={c.key}
              className={cn(
                "relative grid grid-cols-[1.5rem_1fr_auto] gap-x-3 py-3",
                outcome === "notEvaluated" && "opacity-55",
              )}
            >
              {i < CONJUNCTS.length - 1 ? (
                <span aria-hidden className="absolute top-9 bottom-0 start-[0.71rem] w-px bg-border-subtle" />
              ) : null}
              <span
                aria-hidden
                className={cn(
                  "mt-0.5 flex size-6 items-center justify-center rounded-full border text-2xs tabular",
                  outcome === "allow" && "border-success/40 text-success",
                  outcome === "deny" && "border-destructive/50 bg-destructive-soft text-destructive",
                  outcome === "error" && "border-warning/50 bg-warning-soft text-warning",
                  outcome === "notEvaluated" && "border-dashed border-border text-subtle-foreground",
                )}
              >
                {outcome === "allow" ? (
                  <Check className="size-3" />
                ) : outcome === "notEvaluated" ? (
                  <Minus className="size-3" />
                ) : (
                  i + 1
                )}
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-medium">
                  <Trans
                    shouldUnescape
                    tOptions={{ interpolation: { escapeValue: true } }}
                    t={t}
                    i18nKey={c.labelKey}
                    components={{
                      conjunction: <span className="me-1.5 text-2xs font-normal text-subtle-foreground" />,
                    }}
                  />
                </h3>
                <p className="text-xs text-muted-foreground">{t(c.checksKey)}</p>
                {c.key === "access" && result.determiningPolicies.length > 0 ? (
                  <div className="mt-2 grid gap-1">
                    <p className="text-2xs text-subtle-foreground">
                      {allowed ? t("permissions.explain.permittedBy") : t("permissions.explain.determinedBy")}
                    </p>
                    <ul className="divide-y divide-border-subtle rounded-md border border-border-subtle">
                      {result.determiningPolicies.map((p) => (
                        <li
                          key={p.cedarPolicyId}
                          className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2.5 py-1.5 text-xs"
                        >
                          {p.policyId && p.ruleId ? (
                            <Link
                              to="/$orgId/$environmentId/permissions/rules"
                              params={scope}
                              search={{ rule: ruleAnchor(p.policyId, p.ruleId) }}
                              className="inline-flex items-center gap-1 font-mono text-foreground/90 hover:text-primary"
                            >
                              {p.policyId} · {p.ruleId} <ArrowUpRight className="rtl-mirror size-3" aria-hidden />
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">{t("permissions.explain.hostPolicy")}</span>
                          )}
                          <code
                            className="ms-auto font-mono text-2xs text-subtle-foreground"
                            title={t("permissions.explain.cedarPolicyId")}
                          >
                            {p.cedarPolicyId}
                          </code>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <Badge tone={copy.tone} className="mt-0.5">
                {t(copy.labelKey)}
              </Badge>
            </li>
          )
        })}
      </ol>
      {result.diagnostics.length > 0 ? (
        <div
          role="alert"
          className="mx-5 mb-4 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning"
        >
          <p className="font-medium">{t("permissions.explain.evaluationError")}</p>
          <ul className="mt-1 list-disc ps-4">
            {result.diagnostics.map((d, i) => (
              <li key={i} dir="ltr" className="font-mono text-2xs">
                {d}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <footer className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border-subtle bg-card px-5 py-2.5 text-2xs text-subtle-foreground">
        <span title={result.definitionEpoch.receiptSha256}>
          <Trans
            shouldUnescape
            tOptions={{ interpolation: { escapeValue: true } }}
            t={t}
            i18nKey="permissions.explain.definitionEpoch"
            values={{
              version: result.definitionEpoch.version,
              receipt: result.definitionEpoch.receiptSha256.slice(0, 12),
            }}
            components={{ receiptValue: <code className="font-mono" /> }}
          />
        </span>
        <span>
          {result.permissionEpoch ? (
            <Trans
              shouldUnescape
              tOptions={{ interpolation: { escapeValue: true } }}
              t={t}
              i18nKey="permissions.explain.permissionSnapshot"
              values={{ epoch: result.permissionEpoch }}
              components={{ epochValue: <code className="font-mono" /> }}
            />
          ) : (
            t("permissions.explain.snapshotUnavailable")
          )}
        </span>
      </footer>
    </section>
  )
}

/** Every admitted AccessIR rule, with its compiled Cedar beside it (read-only; never edited here). */
export function RulesPage() {
  const t = useT()
  const scope = useScope()
  const search = useSearch({ from: "/$orgId/$environmentId/permissions/rules" })
  const defs = useDefinitions(scope)
  const [query, setQuery] = useState("")
  const rules = useMemo(() => (defs.data ? accessRules(defs.data) : []), [defs.data])
  const q = query.trim().toLowerCase()
  const shown = q
    ? rules.filter((r) => [r.policyId, r.ruleId, r.resourceType, ...r.actions].some((s) => s.toLowerCase().includes(q)))
    : rules
  const policies = [...new Set(shown.map((r) => r.policyId))]
  const focus = search.rule

  useEffect(() => {
    if (focus) document.getElementById(`rule:${focus}`)?.scrollIntoView({ block: "center" })
  }, [focus, rules.length])

  if (defs.isPending) return <TableSkeleton rows={6} columns={3} />
  if (defs.isError)
    return (
      <ErrorState
        error={defs.error}
        what={t("permissions.rules.installedRules")}
        onRetry={() => void defs.refetch()}
        inline
      />
    )
  if (rules.length === 0) {
    return (
      <EmptyState icon={<KeyRound />} title={t("permissions.rules.noneInstalled")}>
        <p>{t("permissions.rules.installHelp")}</p>
      </EmptyState>
    )
  }
  return (
    <div className="grid gap-5">
      <div className="flex items-center gap-3">
        <div className="relative w-72">
          <Search
            className="pointer-events-none absolute top-1/2 start-2.5 size-3.5 -translate-y-1/2 text-subtle-foreground"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("permissions.rules.filterPlaceholder")}
            aria-label={t("permissions.rules.filter")}
            className={cn(controlClass, "ps-8")}
          />
        </div>
        <span className="text-xs text-subtle-foreground tabular">
          {t("permissions.rules.count", { shown: shown.length, count: rules.length })}
        </span>
      </div>
      <UnavailableBlock why={{ reason: t("permissions.rules.compiledCedarHelp"), issue: CEDAR_ISSUE }} />
      {policies.map((policyId) => {
        const inPolicy = shown.filter((r) => r.policyId === policyId)
        const artifactId = inPolicy[0]?.artifactId ?? policyId
        return (
          <section key={policyId} aria-label={t("permissions.rules.policy", { id: policyId })}>
            <div className="mb-2 flex items-baseline gap-2">
              <h2 className="font-mono text-sm">{policyId}</h2>
              <Link
                to="/$orgId/$environmentId/definitions/$kind/$artifactId"
                params={{ ...scope, kind: "policy", artifactId }}
                className="text-2xs text-subtle-foreground hover:text-primary"
              >
                {artifactId} <ArrowUpRight className="rtl-mirror inline size-3" aria-hidden />
              </Link>
            </div>
            <ul className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle">
              {inPolicy.map((r) => {
                const anchor = ruleAnchor(r.policyId, r.ruleId)
                return (
                  <li
                    key={anchor}
                    id={`rule:${anchor}`}
                    aria-current={focus === anchor ? "true" : undefined}
                    className={cn(
                      "grid gap-4 px-4 py-3 md:grid-cols-[1fr_1fr]",
                      focus === anchor && "bg-primary-soft shadow-[inset_2px_0_0_var(--primary)]",
                    )}
                  >
                    <div className="min-w-0">
                      <h3 className="font-mono text-sm">{r.ruleId}</h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        <Trans
                          shouldUnescape
                          tOptions={{ interpolation: { escapeValue: true } }}
                          t={t}
                          i18nKey="permissions.rules.when"
                          values={{ condition: describeRuleCondition(r.rule, t) }}
                          components={{ conditionValue: <span className="text-foreground/90" /> }}
                        />
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                        {r.actions.map((a) => (
                          <code
                            key={a}
                            className="rounded-sm bg-raised px-1.5 py-0.5 font-mono text-2xs text-foreground/85"
                          >
                            {a}
                          </code>
                        ))}
                        <span className="text-subtle-foreground">{t("permissions.rules.on")}</span>
                        <code className="font-mono text-2xs text-foreground/85">{r.resourceType}</code>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="mb-1 text-2xs text-subtle-foreground">{t("permissions.rules.compiledCedar")}</p>
                      <UnavailableBlock
                        compact
                        why={{ reason: t("permissions.rules.notServed"), issue: CEDAR_ISSUE }}
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

/** People: yourself first, then any person by key. */
export function PeoplePage() {
  const t = useT()
  const scope = useScope()
  const navigate = useNavigate()
  const me = usePrincipal(scope)
  const defs = useDefinitions(scope)
  const personTypeAbsent =
    defs.isSuccess && defs.data.unreadable === 0 && !objectTypes(defs.data).some((type) => type.name === PERSON_TYPE)
  // Missing definition visibility is not evidence that the person type is absent.
  const people = useObjects(scope, PERSON_TYPE, null, 50, !defs.isPending && !personTypeAbsent)
  const [key, setKey] = useState("")
  const open = (personId: string) =>
    void navigate({ to: "/$orgId/$environmentId/permissions/people/$personId", params: { ...scope, personId } })
  return (
    <div className="grid gap-6">
      {me.status === "ready" ? (
        <section
          aria-label={t("permissions.people.connectionPrincipal")}
          className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3 rounded-lg border border-border-subtle bg-card px-4 py-3 sm:grid-cols-[auto_minmax(0,1fr)_auto]"
        >
          <UserRound className="size-4 text-subtle-foreground" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">{t("permissions.people.connectionActsAs")}</p>
            <p className="break-all font-mono text-sm">{me.actor}</p>
          </div>
          {me.personKey ? (
            <Button
              size="sm"
              className="col-span-2 justify-self-start sm:col-span-1"
              onClick={() => open(me.personKey as string)}
            >
              {t("permissions.people.yourAccess")} <ArrowUpRight className="rtl-mirror" />
            </Button>
          ) : (
            <span className="col-span-2 text-xs text-subtle-foreground sm:col-span-1">
              {t("permissions.people.notPerson")}
            </span>
          )}
        </section>
      ) : me.status === "failed" ? (
        <ErrorState error={me.error} what={t("permissions.people.connectionPrincipal")} inline />
      ) : null}
      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (key.trim()) open(key.trim())
        }}
      >
        <div className="grid min-w-0 flex-1 gap-1.5 sm:flex-none">
          <Label htmlFor="person-key">{t("permissions.people.openPerson")}</Label>
          <Input
            id="person-key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="person:…"
            className={cn(controlClass, "w-full font-mono text-xs sm:w-72")}
            spellCheck={false}
          />
        </div>
        <Button type="submit" disabled={!key.trim()}>
          {t("permissions.people.open")}
        </Button>
      </form>
      <section aria-label={t("permissions.people.readablePeople")}>
        <h2 className="mb-2 text-sm font-medium">{t("permissions.people.readablePeople")}</h2>
        {personTypeAbsent ? (
          <UnavailableBlock why={{ reason: t("permissions.people.typeNotInstalled", { objectType: PERSON_TYPE }) }} />
        ) : people.isPending ? (
          <TableSkeleton rows={3} columns={2} />
        ) : people.isError ? (
          <ErrorState error={people.error} what={t("permissions.people.people")} inline />
        ) : people.data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("permissions.people.noneVisible")}</p>
        ) : (
          <ul className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle">
            {people.data.items.map((p) => (
              <li key={p.key}>
                <Link
                  to="/$orgId/$environmentId/permissions/people/$personId"
                  params={{ ...scope, personId: p.key }}
                  className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-raised"
                >
                  <code className="font-mono text-xs">{p.key}</code>
                  <span className="truncate text-muted-foreground">{displayName(p.values)}</span>
                  <ArrowUpRight className="rtl-mirror ms-auto size-3.5 text-subtle-foreground" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        )}
        {!personTypeAbsent && people.data?.nextCursor ? (
          <p className="mt-2 text-2xs text-subtle-foreground">{t("permissions.people.morePeople")}</p>
        ) : null}
      </section>
    </div>
  )
}

function displayName(values: unknown): string {
  if (typeof values !== "object" || values === null) return ""
  const v = values as Record<string, unknown>
  const name = v.displayName ?? v.display_name ?? v.name ?? v.fullName
  return typeof name === "string" ? name : ""
}

/** One person: effective memberships and the delegations they granted, with revoke. */
export function PersonPage() {
  const t = useT()
  const scope = useScope()
  const { personId } = useParams({ from: "/$orgId/$environmentId/permissions/people/$personId" })
  const memberships = useMemberships(scope, personId)
  const delegations = useDelegations(scope, personId)
  return (
    <div className="grid gap-8">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/$orgId/$environmentId/permissions/people"
          params={scope}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {t("permissions.people")}
        </Link>
        <span className="text-subtle-foreground">/</span>
        <h2 className="font-mono text-base">{personId}</h2>
        <Button size="sm" variant="ghost" asChild className="ms-auto">
          <Link to="/$orgId/$environmentId/permissions/explain" params={scope} search={{ principal: personId }}>
            {t("permissions.people.explainPerson")} <ArrowUpRight className="rtl-mirror" />
          </Link>
        </Button>
      </div>
      <section aria-label={t("permissions.membership.memberships")}>
        <div className="mb-2 flex items-baseline gap-3">
          <h3 className="text-sm font-medium">{t("permissions.membership.memberships")}</h3>
          <p className="text-xs text-muted-foreground">
            <Trans
              shouldUnescape
              tOptions={{ interpolation: { escapeValue: true } }}
              t={t}
              i18nKey="permissions.membership.namespaceHelp"
              components={{
                desiredValue: <Badge tone="outline" className="border-dashed" />,
                observedValue: <Badge />,
              }}
            />
          </p>
        </div>
        {memberships.isPending ? (
          <TableSkeleton rows={3} columns={6} />
        ) : memberships.isError ? (
          <ErrorState
            error={memberships.error}
            what={t("permissions.membership.personMemberships", { personId })}
            onRetry={() => void memberships.refetch()}
            inline
          />
        ) : memberships.data.items.length === 0 ? (
          <p className="rounded-lg border border-border-subtle px-4 py-3 text-sm text-muted-foreground">
            {t("permissions.membership.noneVisible")}
          </p>
        ) : (
          <MembershipTable rows={memberships.data.items} />
        )}
      </section>
      <section aria-label={t("permissions.delegation.delegations")}>
        <div className="mb-2 flex items-baseline gap-3">
          <h3 className="text-sm font-medium">{t("permissions.delegation.granted")}</h3>
          <p className="text-xs text-muted-foreground">{t("permissions.delegation.help")}</p>
        </div>
        {delegations.isPending ? (
          <TableSkeleton rows={2} columns={5} />
        ) : delegations.isError ? (
          <ErrorState
            error={delegations.error}
            what={t("permissions.delegation.personDelegations", { personId })}
            onRetry={() => void delegations.refetch()}
            inline
          />
        ) : delegations.data.items.length === 0 ? (
          <p className="rounded-lg border border-border-subtle px-4 py-3 text-sm text-muted-foreground">
            {t("permissions.delegation.noneGranted")}
          </p>
        ) : (
          <ul className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle">
            {delegations.data.items.map((d) => (
              <DelegationRow key={d.id} delegation={d} grantorId={personId} readAt={delegations.dataUpdatedAt} />
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function MembershipTable({ rows }: { rows: readonly Membership[] }) {
  const t = useT()
  return (
    <HairlineTable
      rows={[...rows]}
      getRowKey={(row) => row.id}
      columns={[
        {
          key: "grants",
          header: t("permissions.membership.grants"),
          cell: (row) => (
            <>
              <div className="flex flex-wrap gap-1.5">
                {membershipGrant(row, t).map((grant) => (
                  <span key={`${grant.kind}:${grant.id}`} className="inline-flex items-center gap-1 text-xs">
                    <span className="text-muted-foreground">{grant.kind}</span>
                    <code className="font-mono">{grant.id}</code>
                  </span>
                ))}
              </div>
              <code className="font-mono text-2xs text-muted-foreground">{row.id}</code>
            </>
          ),
        },
        {
          key: "source",
          header: t("explorer.source"),
          cell: (row) =>
            row.namespace === "desired" ? (
              <Badge tone="outline" className="border-dashed">
                desired
              </Badge>
            ) : (
              <Badge>observed</Badge>
            ),
        },
        {
          key: "status",
          header: t("findings.filterStatus"),
          cell: (row) => (
            <span className="text-xs">
              <span className={cn(row.status !== "active" && "text-warning")}>{row.status}</span>
              <span className="text-muted-foreground"> · {row.verificationState}</span>
            </span>
          ),
        },
        {
          key: "validity",
          header: t("permissions.membership.valid"),
          cell: (row) => (
            <span className="text-xs tabular-nums">
              {validity(row.validFrom, row.validUntil, t)}
              {!row.current ? (
                <span className="block text-2xs text-warning">{t("permissions.membership.outsideWindow")}</span>
              ) : null}
            </span>
          ),
        },
        {
          key: "authority",
          header: t("permissions.membership.authority"),
          cell: (row) => <code className="font-mono text-2xs">{row.authority}</code>,
        },
        {
          key: "effective",
          header: t("permissions.membership.inEffectColumn"),
          cell: (row) =>
            row.effective ? (
              <Badge tone="success">{t("permissions.membership.inEffect")}</Badge>
            ) : (
              <Badge>{t("permissions.membership.notInEffect")}</Badge>
            ),
        },
      ]}
    />
  )
}

const dateTime = (ms: number) => `${new Date(ms).toISOString().replace("T", " ").slice(0, 16)} UTC`

/** `readAt` is when the list was read: expiry is judged against the read, not a moving clock. */
function DelegationRow({
  delegation: d,
  grantorId,
  readAt,
}: {
  delegation: Delegation
  grantorId: string
  readAt: number
}) {
  const t = useT()
  const scope = useScope()
  const revoke = useRevokeDelegation(scope, grantorId)
  const [confirming, setConfirming] = useState(false)
  const [typed, setTyped] = useState("")
  const state = delegationState(d, readAt)
  const refused = revoke.isError ? refusalOfError(revoke.error) : null
  const { read, action } = d.definition
  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <code className="font-mono text-sm">{d.id}</code>
            <Badge tone={state === "active" ? "success" : state === "revoked" ? "danger" : "neutral"}>{state}</Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            <Trans
              shouldUnescape
              tOptions={{ interpolation: { escapeValue: true } }}
              t={t}
              i18nKey="permissions.delegation.authoritySentence"
              values={{
                executor: d.executor,
                read: `${read.objectType} / ${read.key}`,
                action: action.actionId,
                target: `${action.target.objectType} / ${action.target.key}`,
              }}
              components={{
                executorValue: <code className="font-mono text-foreground/85" />,
                readValue: (
                  <Link
                    to="/$orgId/$environmentId/objects/$objectType/$key"
                    params={{ ...scope, objectType: read.objectType, key: read.key }}
                    className="font-mono text-foreground/85 hover:text-primary"
                  />
                ),
                actionValue: <code className="font-mono text-foreground/85" />,
                targetValue: <code className="font-mono text-foreground/85" />,
              }}
            />
          </p>
          <p className="mt-0.5 text-2xs text-subtle-foreground">
            <Trans
              shouldUnescape
              tOptions={{ interpolation: { escapeValue: true } }}
              t={t}
              i18nKey={
                state === "revoked" && d.revocation
                  ? "permissions.delegation.approvedRevoked"
                  : state === "expired"
                    ? "permissions.delegation.approvedExpired"
                    : "permissions.delegation.approvedExpires"
              }
              values={{
                approvedBy: d.approvedBy,
                revokedAt: d.revocation ? dateTime(d.revocation.revokedAtMs) : "",
                revokedBy: d.revocation?.revokedBy ?? "",
                expiresAt: state === "revoked" && d.revocation ? "" : dateTime(d.expiresAtMs),
              }}
              components={{ approverValue: <code className="font-mono" /> }}
            />
          </p>
        </div>
        {state === "active" && !confirming ? (
          <Button size="sm" variant="destructive" onClick={() => setConfirming(true)}>
            {t("permissions.delegation.revoke")}
          </Button>
        ) : null}
      </div>
      {state === "active" && confirming ? (
        <form
          role="group"
          aria-label={t("permissions.delegation.confirmRevoking", { id: d.id })}
          className="mt-3 grid gap-2 rounded-md border border-destructive/30 bg-destructive-soft/40 p-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (typed === d.id) revoke.mutate(d.id)
          }}
        >
          <p className="text-sm text-foreground">{t("permissions.delegation.revokeQuestion")}</p>
          <p className="text-xs text-muted-foreground">
            <Trans
              shouldUnescape
              tOptions={{ interpolation: { escapeValue: true } }}
              t={t}
              i18nKey="permissions.delegation.revokeConsequence"
              values={{ executor: d.executor, id: d.id }}
              components={{
                executorValue: <code className="font-mono" />,
                idValue: <code className="font-mono text-foreground" />,
              }}
            />
          </p>
          <div className="flex items-center gap-2">
            <Input
              aria-label={t("permissions.delegation.confirmId")}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className={cn(controlClass, "w-64 font-mono text-xs")}
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
            <Button type="submit" size="sm" variant="destructive" disabled={typed !== d.id || revoke.isPending}>
              {revoke.isPending ? <RotateCw className="animate-spin" /> : null}{" "}
              {t("permissions.delegation.revokeDelegation")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setConfirming(false)
                setTyped("")
                revoke.reset()
              }}
            >
              {t("common.cancel")}
            </Button>
          </div>
          {revoke.isError ? (
            <p role="alert" className="text-xs text-destructive">
              {refused ? (
                <>
                  {refused.refusal.message}: {refused.refusal.explanation}{" "}
                  <code className="font-mono text-2xs">
                    {refused.refusal.code} · {refused.refusal.category}
                  </code>
                  {refused.refusal.code === "delegation.not_found" ? t("permissions.delegation.notFound") : null}
                </>
              ) : (
                t("permissions.delegation.revocationUncertain", {
                  error:
                    revoke.error instanceof CellUnreachable
                      ? t("common.couldNotReach", { url: revoke.error.baseUrl })
                      : revoke.error instanceof Error
                        ? revoke.error.message
                        : String(revoke.error),
                })
              )}
            </p>
          ) : null}
        </form>
      ) : null}
      {revoke.isSuccess && revoke.data.revocation ? (
        <p role="status" className="mt-2 text-xs text-success">
          {t("permissions.delegation.revokedAt", { date: dateTime(revoke.data.revocation.revokedAtMs) })}
        </p>
      ) : null}
    </li>
  )
}
